<script setup lang="ts">
import { parseISO } from "date-fns";
import { computed, ref, toValue } from "vue";
import { useRouter } from "vue-router";
import SearchIcon from "@/components/shared/icons/SearchIcon.vue";
import { useTenantTheming } from "@/composables/useTenantTheming.js";
import { ORDER_STATUS } from "@/constants";
import useExperiences from "./composables/useExperiences.js";
import MobileAppBanner from "./DownloadMobileApp.vue";
import MyExperiencesLoading from "./MyExperiencesLoading/index.vue";
import MyExperiencesOverview from "./MyExperiencesOverview.vue";
import NoExperiences from "./NoExperiences.vue";

const router = useRouter();

const activeSessionNames = ref<string[]>([]);
const hasActiveExperiences = ref<boolean>(true);
const cardActionLoading = ref<boolean>(false);

const { experiences, experiencesIsLoading, profile, profileDevices } = useExperiences();
const { secondaryColorClassButton } = useTenantTheming();

const isLoading = computed<boolean>(() => {
  return toValue(experiencesIsLoading) || cardActionLoading.value;
});

const onCardActionStart = (): void => {
  cardActionLoading.value = true;
};

const userName = computed<string>(() => {
  const preferred = profile.value?.preferred_first_name?.trim();
  const legal = profile.value?.legal_first_name?.trim();
  return preferred?.length ? preferred : legal;
});

const hasExperiences = computed<boolean>(() => !experiencesIsLoading.value && experiences.value?.length > 0);

const showMobileBanner = computed<boolean>(() => {
  const upcomingExperiences =
    experiences.value?.filter(
      (experience) =>
        [ORDER_STATUS.submitted, ORDER_STATUS.paidConfirmed, ORDER_STATUS.fulfilled].includes(
          experience.order_status,
        ) &&
        parseISO(experience.start_date) >= new Date() &&
        experience.show_mobile_app_promotions,
    ) ?? [];
  return (profileDevices.value?.length ?? 0) === 0 && hasExperiences.value && upcomingExperiences.length > 0;
});

const welcomeMessageLabel = computed<string>(() => {
  return userName.value ? `Hello, ${userName.value}!` : "";
});

const updateMobileBanner = (sessionNames: string[], hasActive: boolean): void => {
  activeSessionNames.value = sessionNames;
  hasActiveExperiences.value = hasActive;
};

const redirectToResults = () => {
  router.push({ name: "results" });
};
</script>
<template>
  <my-experiences-loading v-if="isLoading" :is-loading="isLoading" message="Loading My Experiences.  Please wait." />
  <div v-show="!isLoading" id="my-experiences-page" class="space-y-6 p-5 bg-blue-100">
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
      <h1
        data-testid="my-experiences-welcome-message"
        class="font-bold text-xl sm:text-4xl leading-[48px] capitalize text-university-primary"
      >
        {{ welcomeMessageLabel }}
      </h1>
      <button
        v-if="hasExperiences"
        data-testid="my-experiences-search-button"
        class="text-sm font-semibold rounded-md px-3 py-2 w-max border transition-colors"
        :class="secondaryColorClassButton"
        @click="redirectToResults"
      >
        <div class="flex flex-row flex-nowrap uppercase items-center gap-2">
          <search-icon :size="17" /> Search for a New Program
        </div>
      </button>
    </div>
    <mobile-app-banner
      v-if="showMobileBanner"
      :session-names="activeSessionNames"
      :show-active-msg="hasActiveExperiences"
    />
    <div id="my-experiences-body">
      <my-experiences-overview
        v-if="hasExperiences"
        :experiences="experiences"
        @update-mobile-banner="updateMobileBanner"
        @start-loading="onCardActionStart"
      />
      <no-experiences v-else />
    </div>
  </div>
</template>
